'use client';

import { EMAIL_INPUT_PATTERN, formatEmail } from '@/components/Form/EmailInput';
import { useJump } from '@/components/MagicJump';
import SubscribeConsentDialog from '@/components/Subscribe/SubscribeConsentDialog';
import { useSubscribeExposureTracking } from '@/components/Subscribe/useSubscribeExposureTracking';
import { toast } from '@/components/Toast';
import { useRouteCnName } from '@/hooks/useRouteMetadata';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import { withSpmCnt, useSpmDomAttr } from '@/reporting/spm';
import { getSpmPositionCode, useGlobalSpmTerminal } from '@/components/Layout/spmTerminal';
import { EPlatform } from '@/utils/enums/platform-enum';
import { sensorsTrack } from '@/utils/sensors';
import { relationEmailMaxLength } from '@/utils/tools';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

const Subscribe_US = () => {
  const token = useStore((state) => state.token);
  const storeAccountEmail = useStore((state) => state.userInfo?.email || '');
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);
  const getMarketingStatus = useStore((state) => state.getMarketingStatus);
  const setLoginRegisterShow = useStore((state) => state.setShowLoginRegisterDialog);
  const t = useTranslations('layout.footer');
  const tEmailValidation = useTranslations('components.form.email.validation');
  const toJumpTarget = useJump();
  const pathname = usePathname();
  const pageName = useRouteCnName();
  // WAP 订阅区仅渲染于移动端；B 位固定 global_wap，footer_subscribe.submit_subscription 附加在既有 EDMModule_Click 上。
  const spmPage = useGlobalSpmTerminal(EPlatform.WAP);
  // Data SPM DOM 打标：与 handleSubscribeButtonClick 上报的 submit_subscription 同源同值
  // （email_input 无既有埋点事件，按约定不打标）。
  const submitSubscriptionDomSpm = useSpmDomAttr(
    getSpmPositionCode(spmPage, 'footer_subscribe', 'submit_subscription'),
  );
  const [email, setEmail] = useState('');
  const [consentDialogVisible, setConsentDialogVisible] = useState(false);
  const isLoggedIn = Boolean(token);

  useEffect(() => {
    if (storeAccountEmail) {
      setEmail(storeAccountEmail);
    } else if (!token) {
      setEmail('');
    }
  }, [storeAccountEmail, token]);

  useEffect(() => {
    if (!token || storeAccountEmail) return;

    refreshAccountInfo();
  }, [refreshAccountInfo, storeAccountEmail, token]);

  const trackEDMModuleExp = () => {
    const spmCnt = getSpmPositionCode(spmPage, 'footer_subscribe', 'code');
    sensorsTrack('EDMModule_Exp', {
      page_name: pageName,
      ...(spmCnt ? { spm_cnt: spmCnt } : {}),
    });
  };

  const trackEDMModuleClick = () => {
    // footer_subscribe.submit_subscription：既有订阅点击事件附加坑位编码；
    // 表外时为 undefined，保持原 properties。
    const spmCnt = getSpmPositionCode(spmPage, 'footer_subscribe', 'submit_subscription');
    const properties = {
      page_name: pageName,
      btn_name: t('subscribe.subscribeButton'),
    };
    sensorsTrack('EDMModule_Click', spmCnt ? withSpmCnt(properties, spmCnt) : properties);
  };

  const { ref: exposureRef } = useSubscribeExposureTracking({
    callback: trackEDMModuleExp,
    pathname,
  });

  const handleOnTabSubscribe = async () => {
    if (isLoggedIn) {
      const currentMarketingStatus = await getMarketingStatus();
      if (!currentMarketingStatus?.marketing_granted?.email) {
        setConsentDialogVisible(true);
        return;
      }

      toJumpTarget({
        Jump_target: '/account/my-account',
        jump_type: 4,
      });
      return;
    }

    const submitEmail = email.trim();
    if (!EMAIL_INPUT_PATTERN.test(submitEmail)) {
      toast.error(tEmailValidation('invalid'));
      return;
    }

    setEmail(submitEmail);
    setLoginRegisterShow({
      show: true,
      initialEmail: submitEmail,
    });
  };

  const handleSubscribeButtonClick = () => {
    trackEDMModuleClick();
    void handleOnTabSubscribe();
  };

  return (
    <>
      <section ref={exposureRef} className="flex w-full flex-col items-center gap-[16px] py-[16px]">
        <p className="w-full text-center font-br text-[14px] font-normal leading-[17px] text-primary-white">
          Missed a deal? Subscribe to never <br /> miss a discount again.
        </p>
        <div className="relative flex h-[50px] w-full items-center rounded-[6px] border border-solid border-[#d1d9e0] bg-primary-white px-[12px] py-[6px]">
          <input
            type="email"
            maxLength={50 + relationEmailMaxLength}
            value={email}
            disabled={isLoggedIn}
            onChange={(event) => setEmail(formatEmail(event.target.value))}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                void handleOnTabSubscribe();
              }
            }}
            placeholder={t('subscribe.emailPlaceholder')}
            className="h-full min-w-0 flex-1 bg-transparent pr-[104px] font-br text-[14px] font-normal leading-[17px] text-[#282828] outline-none placeholder:text-[#6c6c6c] disabled:cursor-not-allowed"
          />
          <button
            type="button"
            className="absolute right-[5px] top-1/2 flex h-[38px] w-[96px] -translate-y-1/2 items-center justify-center rounded-[4px] bg-primary-purple px-[16px] py-[10px] font-br text-[12px] font-normal leading-[14px] text-primary-white"
            onClick={handleSubscribeButtonClick}
            {...submitSubscriptionDomSpm}
          >
            {t('subscribe.subscribeButton')}
          </button>
        </div>
      </section>
      <SubscribeConsentDialog open={consentDialogVisible} onOpenChange={setConsentDialogVisible} />
    </>
  );
};

export default Subscribe_US;
